import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { Icon } from "@iconify/react";
import {
  FaWeight,
  FaSmile,
  FaHandHoldingMedical,
  FaRegCircle,
  FaChartLine,
  FaCube,
  FaHeart,
  FaRegHeart,
  FaTimesCircle,
} from "react-icons/fa";
import {
  fetchProductDetail,
  selectProductDetail,
  selectProductDetailStatus,
  selectProductDetailError,
  clearProductDetail,
} from "../redux/productDetailSlice";
import FrequentlyBoughtBundles from "./FrequentlyBoughtBundles";
import Login from "./Login";
import { v4 as uuidv4 } from "uuid";

// ===========================================
// GUEST CART FUNCTIONS - SAME AS Shop.jsx
// ===========================================
const GUEST_CART_KEY = "guest_cart";
const sendPurchaseEvent = async (value) => {
  const eventId = uuidv4();

  if (window.fbq) {
    window.fbq(
      "track",
      "Purchase",
      {
        value: value,
        currency: "INR",
      },
      {
        eventID: eventId,
      },
    );
  }

  await fetch("https://evercorenutrition.com/ev_admin/api/fb-event/", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      event_id: eventId,
      value: value,
    }),
  });
};
const getGuestCart = () => {
  try {
    const cart = localStorage.getItem(GUEST_CART_KEY);
    return cart ? JSON.parse(cart) : [];
  } catch (error) {
    console.error("Error getting guest cart:", error);
    return [];
  }
};

const saveGuestCart = (cartItems) => {
  try {
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cartItems));
  } catch (error) {
    console.error("Error saving guest cart:", error);
  }
};

const addProductToGuestCart = (
  productId,
  variantId,
  quantity,
  productDetails,
) => {
  const guestCart = getGuestCart();
  const existingItemIndex = guestCart.findIndex(
    (item) =>
      item.product_id === productId &&
      item.variant_id === variantId &&
      item.item_type === "product",
  );

  if (existingItemIndex !== -1) {
    guestCart[existingItemIndex].quantity += quantity;
    // console.log(
    //   "Updated existing product in guest cart:",
    //   guestCart[existingItemIndex],
    // );
  } else {
    guestCart.push({
      product_id: productId,
      variant_id: variantId,
      quantity: quantity,
      name: productDetails.name,
      unit_price: parseFloat(productDetails.selling_price),
      price: parseFloat(productDetails.selling_price),
      image: productDetails.main_image,
      added_at: new Date().toISOString(),
      item_type: "product",
    });
    // console.log("Added new product to guest cart:", {
    //   productId,
    //   variantId,
    //   quantity,
    //   productDetails,
    // });
  }

  saveGuestCart(guestCart);
  // console.log("Guest cart after adding product:", getGuestCart());
  return guestCart;
};

const mergeGuestCartWithUserCart = async (customerId, token) => {
  const guestCart = getGuestCart();

  if (!guestCart || guestCart.length === 0) {
    return { success: true, message: "No guest cart to merge" };
  }

  // console.log("Merging guest cart:", guestCart);
  let mergedCount = 0;

  try {
    for (const item of guestCart) {
      let body = {};

      if (item.item_type === "combo") {
        body = {
          customer_id: String(customerId),
          combo_id: item.combo_id,
          quantity: item.quantity,
        };
      } else {
        body = {
          customer_id: String(customerId),
          product_id: item.product_id,
          variant_id: item.variant_id,
          quantity: item.quantity,
        };
      }

      const response = await fetch(
        "https://evercorenutrition.com/ev_admin/api/cart/",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        },
      );

      if (response.ok) {
        mergedCount++;
      }
    }

    // Clear guest cart after successful merge
    localStorage.removeItem(GUEST_CART_KEY);
    // console.log(`Merged ${mergedCount} items from guest cart`);

    return {
      success: true,
      message: `Merged ${mergedCount} items from guest cart`,
    };
  } catch (error) {
    console.error("Error merging guest cart:", error);
    return { success: false, message: "Failed to merge guest cart" };
  }
};

// ─── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`custom-toast ${type}`}>
      <div className="toast-content">
        {type === "success" && <i className="fas fa-check-circle"></i>}
        {type === "error" && <i className="fas fa-exclamation-circle"></i>}
        {type === "info" && <i className="fas fa-info-circle"></i>}
        {type === "warning" && <i className="fas fa-exclamation-triangle"></i>}
        <span>{message}</span>
      </div>
      <button className="toast-close" onClick={onClose}>
        ×
      </button>
    </div>
  );
};

// ─── Star displays ─────────────────────────────────────────────────────────────
const StarRating = ({ rating }) => (
  <div style={{ display: "flex", gap: "2px" }}>
    {[1, 2, 3, 4, 5].map((s) => (
      <span key={s} style={{ color: s <= rating ? "#FFD700" : "#ddd" }}>
        ★
      </span>
    ))}
  </div>
);

const InteractiveStarRating = ({
  rating,
  setRating,
  hoverRating,
  setHoverRating,
}) => (
  <div style={{ display: "flex", gap: "4px" }}>
    {[1, 2, 3, 4, 5].map((s) => (
      <span
        key={s}
        onClick={() => setRating(s)}
        onMouseEnter={() => setHoverRating(s)}
        onMouseLeave={() => setHoverRating(0)}
        style={{
          cursor: "pointer",
          fontSize: "24px",
          color: s <= (hoverRating || rating) ? "#FFD700" : "#ddd",
          transition: "color 0.1s ease",
        }}
      >
        ★
      </span>
    ))}
  </div>
);

// ─── Feature icon map (by index) ──────────────────────────────────────────────
const FEATURE_ICONS = [
  <FaWeight size={34} />,
  <FaSmile size={34} />,
  <FaHandHoldingMedical size={34} />,
  <FaRegCircle size={34} />,
  <FaChartLine size={34} />,
  <FaCube size={34} />,
  <FaWeight size={34} />,
];

// ─── Certifications ────────────────────────────────────────────────────────────
const certifications = [
  { img: "/assets/images/certificate/c1.png", alt: "FDA" },
  { img: "/assets/images/certificate/c2.png", alt: "HACCP" },
  { img: "/assets/images/certificate/c3.png", alt: "GMP" },
  { img: "/assets/images/certificate/c4.png", alt: "ISO" },
  { img: "/assets/images/certificate/c5.png", alt: "FSSAI" },
  { img: "/assets/images/certificate/c6.png", alt: "Non GMO" },
];

// ──────────────────────────────────────────────────────────────────────────────
const Productdetails = () => {
  const [showFullDesc, setShowFullDesc] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const dispatch = useDispatch();
  const { id } = useParams();

  const product = useSelector(selectProductDetail);
  const status = useSelector(selectProductDetailStatus);
  const error = useSelector(selectProductDetailError);

  // ── UI state ──
  const [selectedFlavour, setSelectedFlavour] = useState("");
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [mainImage, setMainImage] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);

  // accordion
  const [activeIndex, setActiveIndex] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [activeFaqIndex, setActiveFaqIndex] = useState(null);
  const [showFaqs, setShowFaqs] = useState(false);
  const [openIndex, setOpenIndex] = useState(null);

  // review form
  const [showForm, setShowForm] = useState(false);
  const [reviewText, setReviewText] = useState("");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [submittingReview, setSubmittingReview] = useState(false);

  // reviews list
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState(null);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [isFetchingReviews, setIsFetchingReviews] = useState(false);

  // cart / wishlist
  const [quantities, setQuantities] = useState({});
  const [wishlist, setWishlist] = useState([]);
  const [wishlistLoading, setWishlistLoading] = useState({});
  const [toast, setToast] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [warningProduct, setWarningProduct] = useState(null);

  // core / how-different
  const [coreItems, setCoreItems] = useState([]);
  const [howWeAreDifferentImage, setHowWeAreDifferentImage] = useState("");
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [copiedCoupon, setCopiedCoupon] = useState(null);
  const [currentPromoIndex, setCurrentPromoIndex] = useState(0);

  const [showLoginPopup, setShowLoginPopup] = useState(false);

  // ── Create allImages array ──
  // const allImages = mainImage || product?.images?.length? [
  //         { id: "main", image: mainImage, alt: product?.name || "" },
  //         ...(product?.images || []).map((img, idx) => ({
  //           id: img.id || idx,
  //           image: img.image || img.image_url,
  //           alt: product?.name || "",
  //         })),
  //       ]
  //     : [];

  // NEW CODE (આ નાખો)
const allImages = React.useMemo(() => {
  const images = [];
  const seenUrls = new Set();
  
  if (mainImage) {
    seenUrls.add(mainImage);
    images.push({ id: "main", image: mainImage, alt: product?.name || "" });
  }
  
  if (product?.images?.length) {
    product.images.forEach((img, idx) => {
      const imgUrl = img.image || img.image_url;
      if (imgUrl && !seenUrls.has(imgUrl)) {
        seenUrls.add(imgUrl);
        images.push({ id: img.id || idx, image: imgUrl, alt: product?.name || "" });
      }
    });
  }
  
  return images;
}, [mainImage, product?.images, product?.name]);

  // ── Fetch coupons ──
  const fetchAvailableCoupons = async () => {
    setCouponsLoading(true);
    try {
      const response = await fetch(
        "https://evercorenutrition.com/ev_admin/api/promos/",
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.promocodes) {
          const now = new Date();
          const validCoupons = data.promocodes.filter((coupon) => {
            if (coupon.discount_value <= 0) return false;
            if (coupon.valid_from && coupon.valid_to) {
              const validFrom = new Date(coupon.valid_from);
              const validTo = new Date(coupon.valid_to);
              return now >= validFrom && now <= validTo;
            }
            return true;
          });
          setAvailableCoupons(validCoupons);
          if (validCoupons.length > 0) {
            setCurrentPromoIndex(
              Math.floor(Math.random() * validCoupons.length),
            );
            if (window.couponInterval) clearInterval(window.couponInterval);
            window.couponInterval = setInterval(
              () => {
                setCurrentPromoIndex(
                  (prevIndex) => (prevIndex + 1) % validCoupons.length,
                );
              },
              (data.rotate_interval || 7) * 1000,
            );
          }
        }
      }
    } catch (err) {
      console.error("Error fetching coupons:", err);
    } finally {
      setCouponsLoading(false);
    }
  };

  // ── helpers ──
  const isAuthenticated = () => !!localStorage.getItem("authToken");
  const getCustomerId = () => {
    try {
      const u = localStorage.getItem("userData");
      if (u) {
        const p = JSON.parse(u);
        return p.id || p.customer_id || null;
      }
    } catch (e) {}
    return null;
  };

  const isInStock = useCallback((item) => {
    if (!item) return false;
    if (item.qty !== undefined && item.qty !== null)
      return Number(item.qty) > 0;
    const stockStatus = item.stock;
    if (stockStatus) {
      const s = String(stockStatus).toLowerCase().trim();
      return s !== "stockout" && s !== "out of stock" && s !== "out_of_stock";
    }
    return true;
  }, []);

  const isOutOfStock = useCallback(() => {
    if (product && !isInStock(product)) return true;
    if (selectedVariant && !isInStock(selectedVariant)) return true;
    return false;
  }, [product, selectedVariant, isInStock]);

  const variantIsOut = useCallback(
    (variant) => {
      return !isInStock(variant);
    },
    [isInStock],
  );

  const getImageUrl = (p) => {
    if (!p) return null;
    if (p.startsWith("http")) return p;
    return `https://evercorenutrition.com${p}`;
  };

  const formatDate = (ds) =>
    new Date(ds).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const showToast = (msg, type = "success") => setToast({ message: msg, type });
  const hideToast = () => setToast(null);
  const closeWarning = () => {
    setShowWarning(false);
    setWarningMessage("");
    setWarningProduct(null);
  };

  const copyToClipboard = (code) => {
    navigator.clipboard
      .writeText(code)
      .then(() => {
        setCopiedCoupon(code);
        showToast(`Coupon code ${code} copied!`, "success");
        setTimeout(() => setCopiedCoupon(null), 2000);
      })
      .catch(() => {
        showToast("Failed to copy code", "error");
      });
  };

  // ── Fetch reviews ──
  const fetchReviews = useCallback(async () => {
    if (!id) return;
    if (isFetchingReviews) return;
    setIsFetchingReviews(true);
    setReviewsLoading(true);
    setReviewsError(null);
    try {
      const headers = { "Content-Type": "application/json" };
      const res = await fetch(
        `https://evercorenutrition.com/ev_admin/api/products/${id}/reviews/`,
        { headers },
      );
      if (res.ok) {
        const data = await res.json();
        let arr = Array.isArray(data) ? data : data.reviews || data.data || [];
        const approved = arr.filter(
          (r) => r.status === "approved" || r.status === 1 || !r.status,
        );
        setReviews(approved);
        if (approved.length > 0) {
          const avg =
            approved.reduce((s, r) => s + (r.rating || 0), 0) / approved.length;
          setAverageRating(Math.round(avg * 10) / 10);
        } else setAverageRating(0);
      } else if (res.status === 401) {
        setReviewsError("Unable to load reviews");
      } else setReviewsError(`Error ${res.status}: Could not load reviews`);
    } catch {
      setReviewsError("Network error loading reviews");
    } finally {
      setReviewsLoading(false);
      setIsFetchingReviews(false);
    }
  }, [id, isFetchingReviews]);

  // ── Submit review ──
  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!isAuthenticated()) {
      setWarningMessage("Please login to submit a review");
      setShowWarning(true);
      return;
    }
    if (!reviewText.trim()) {
      setWarningMessage("Please write your review");
      setShowWarning(true);
      return;
    }
    if (rating === 0) {
      setWarningMessage("Please select a rating");
      setShowWarning(true);
      return;
    }
    setSubmittingReview(true);
    try {
      const token = localStorage.getItem("authToken");
      const cid = getCustomerId();
      if (!cid) {
        setWarningMessage("Could not identify customer. Please login again.");
        setShowWarning(true);
        setSubmittingReview(false);
        return;
      }
      const res = await fetch(
        `https://evercorenutrition.com/ev_admin/api/products/${id}/reviews/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ rating, review: reviewText.trim() }),
        },
      );
      const data = await res.json();
      if (res.ok) {
        setShowForm(false);
        setReviewText("");
        setRating(0);
        showToast(
          "Review submitted! It will appear after approval.",
          "success",
        );
        setTimeout(fetchReviews, 2000);
      } else {
        setWarningMessage(
          data.message || data.error || "Failed to submit review",
        );
        setShowWarning(true);
      }
    } catch {
      setWarningMessage("Network error. Please try again.");
      setShowWarning(true);
    } finally {
      setSubmittingReview(false);
    }
  };

  // ── Wishlist functions ──
  const fetchWishlistFromAPI = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      const cid = getCustomerId();
      if (!cid) return;
      const res = await fetch(
        `https://evercorenutrition.com/ev_admin/api/wishlist/?customer_id=${cid}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (res.ok) {
        const d = await res.json();
        setWishlist((d?.wishlist?.products || []).map((i) => i.id));
      } else if (res.status === 401) localStorage.removeItem("authToken");
    } catch (e) {
      console.error(e);
    }
  };

  const addToWishlistAPI = async (productId, quantity = 1) => {
    if (!isAuthenticated()) {
      setWarningMessage("Please login to add items to your wishlist");
      setWarningProduct(productId);
      setShowWarning(true);
      setTimeout(() => {
        setShowLoginPopup(true);
      }, 1000);
      return false;
    }
    if (!selectedVariant) {
      setWarningMessage("Please select a pack size first");
      setShowWarning(true);
      return false;
    }
    setWishlistLoading((p) => ({ ...p, [productId]: true }));
    try {
      const token = localStorage.getItem("authToken");
      const cid = getCustomerId();
      if (!cid) {
        setWarningMessage("Could not identify customer.");
        setShowWarning(true);
        return false;
      }
      const res = await fetch(
        "https://evercorenutrition.com/ev_admin/api/wishlist/",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            customer_id: String(cid),
            product_id: productId,
            variant_id: selectedVariant.id,
            quantity,
          }),
        },
      );
      if (res.ok) {
        setWishlist((p) => [...p, productId]);
        setWarningMessage("Item added to wishlist!");
        setShowWarning(true);
        setTimeout(() => setShowWarning(false), 2000);
        return true;
      } else if (res.status === 401) {
        localStorage.removeItem("authToken");
        setWarningMessage("Session expired.");
        setShowWarning(true);
      } else {
        const d = await res.json().catch(() => ({}));
        setWarningMessage(d.message || d.error || "Failed to add to wishlist.");
        setShowWarning(true);
      }
      return false;
    } catch {
      setWarningMessage("Network error.");
      setShowWarning(true);
      return false;
    } finally {
      setWishlistLoading((p) => ({ ...p, [productId]: false }));
    }
  };

  const removeFromWishlistAPI = async (productId) => {
    if (!isAuthenticated()) {
      setWarningMessage("Please login to manage your wishlist");
      setShowWarning(true);
      return false;
    }
    setWishlistLoading((p) => ({ ...p, [productId]: true }));
    try {
      const token = localStorage.getItem("authToken");
      const cid = getCustomerId();
      if (!cid) {
        setWarningMessage("Could not identify customer.");
        setShowWarning(true);
        return false;
      }
      const res = await fetch(
        `https://evercorenutrition.com/ev_admin/api/wishlist/?customer_id=${cid}&product_id=${productId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (res.ok) {
        setWishlist((p) => p.filter((i) => i !== productId));
        setWarningMessage("Item removed from wishlist");
        setShowWarning(true);
        setTimeout(() => setShowWarning(false), 2000);
        return true;
      } else if (res.status === 401) {
        localStorage.removeItem("authToken");
        setWarningMessage("Session expired.");
        setShowWarning(true);
      } else {
        const d = await res.json().catch(() => ({}));
        setWarningMessage(d.message || d.error || "Failed to remove.");
        setShowWarning(true);
      }
      return false;
    } catch {
      setWarningMessage("Network error.");
      setShowWarning(true);
      return false;
    } finally {
      setWishlistLoading((p) => ({ ...p, [productId]: false }));
    }
  };

  const toggleWishlist = async (pid) => {
    if (!selectedVariant) {
      setWarningMessage("Please select a pack size first");
      setShowWarning(true);
      return;
    }
    if (wishlist.includes(pid)) await removeFromWishlistAPI(pid);
    else await addToWishlistAPI(pid, getQuantity(pid));
  };

  const isInWishlist = (pid) => wishlist.includes(pid);

  // ── Quantity functions ──
  const getQuantity = (pid) => quantities[pid] || 1;
  const decreaseQuantity = (pid) =>
    setQuantities((p) => ({ ...p, [pid]: Math.max(1, (p[pid] || 1) - 1) }));
  const increaseQuantity = (pid) =>
    setQuantities((p) => ({ ...p, [pid]: (p[pid] || 1) + 1 }));

  // ── Add to cart with guest cart support ──
  const addToCart = async (prod) => {
    if (addingToCart) return;

    if (isOutOfStock()) {
      setWarningMessage("This product is out of stock");
      setShowWarning(true);
      return;
    }

    if (!selectedVariant) {
      setWarningMessage("Please select a pack size first");
      setShowWarning(true);
      return;
    }

    setAddingToCart(true);
    const qty = getQuantity(prod.id);

    await sendPurchaseEvent(parseFloat(selectedVariant.price) * qty);

    try {
      if (!isAuthenticated()) {
        // console.log("User not authenticated, saving to guest cart");
        const productDetailsForCart = {
          name: prod.name,
          selling_price: parseFloat(selectedVariant.price),
          main_image: mainImage || prod.main_image,
        };

        addProductToGuestCart(
          prod.id,
          selectedVariant.id,
          qty,
          productDetailsForCart,
        );
        setWarningMessage("Item added to cart!");
        setShowWarning(true);
        setTimeout(() => setShowWarning(false), 2000);
        window.dispatchEvent(new Event("cartUpdated"));
        setAddingToCart(false);
        return;
      }

      // console.log("User authenticated, adding to server cart");
      const token = localStorage.getItem("authToken");
      const cid = getCustomerId();

      if (!cid) {
        setWarningMessage("Could not identify customer.");
        setShowWarning(true);
        setAddingToCart(false);
        return;
      }

      const res = await fetch(
        "https://evercorenutrition.com/ev_admin/api/cart/",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            customer_id: cid,
            product_id: prod.id,
            variant_id: selectedVariant.id,
            quantity: qty,
            unit_price: parseFloat(selectedVariant.price),
          }),
        },
      );

      if (res.ok) {
        setWarningMessage("Item added to cart!");
        setShowWarning(true);
        setTimeout(() => setShowWarning(false), 2000);
        window.dispatchEvent(new Event("cartUpdated"));
      } else {
        const d = await res.json().catch(() => ({}));
        setWarningMessage(d.message || d.error || "Failed to add to cart.");
        setShowWarning(true);
      }
    } catch {
      setWarningMessage("Network error.");
      setShowWarning(true);
    } finally {
      setTimeout(() => {
        setAddingToCart(false);
      }, 500);
    }
  };

  // const shareProduct = (prod) => {
  //   const url = window.location.origin + (prod.link || `/product/${prod.id}`);
  //   if (navigator.share)
  //     navigator.share({
  //       title: prod.name,
  //       text: "Check out this product!",
  //       url,
  //     });
  //   else {
  //     navigator.clipboard.writeText(url);
  //     showToast("Product link copied!", "success");
  //   }
  // };


const shareProduct = (prod) => {
  const url = window.location.origin + (prod.link || `/product/${prod.id}`);
  
  if (navigator.share) {
    navigator.share({
      title: prod.name,
      // text: "Check out this product!",
      url: url,
    }).catch((err) => {
      console.error("Share cancelled or failed:", err);
    });
  } else {
    navigator.clipboard.writeText(url);
    showToast("Product link copied!", "success");
  }
};

// Add this after your other useEffects
useEffect(() => {
  if (!product || !mainImage) return;
  
  const updateMetaTag = (property, content) => {
    if (!content) return;
    let meta = document.querySelector(`meta[property="${property}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('property', property);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };
  
  const updateTwitterTag = (name, content) => {
    if (!content) return;
    let meta = document.querySelector(`meta[name="${name}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', name);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };
  
  // Update OG tags dynamically
  updateMetaTag('og:title', product.name);
  updateMetaTag('og:description', product.short_description || product.description || 'Check out this amazing product');
  updateMetaTag('og:image', getImageUrl(mainImage));
  updateMetaTag('og:url', window.location.href);
  updateMetaTag('og:type', 'product');
  updateMetaTag('og:site_name', 'Evercore Nutrition');
  
  // Twitter cards
  updateTwitterTag('twitter:card', 'summary_large_image');
  updateTwitterTag('twitter:title', product.name);
  updateTwitterTag('twitter:description', product.short_description || product.description);
  updateTwitterTag('twitter:image', getImageUrl(mainImage));
  
}, [product, mainImage]);

  const handleLoginSuccess = useCallback(async (user) => {
    setShowLoginPopup(false);

    const token = localStorage.getItem("authToken");
    const customerId = getCustomerId();

    if (token && customerId) {
      const mergeResult = await mergeGuestCartWithUserCart(customerId, token);
      if (
        mergeResult.success &&
        mergeResult.message !== "No guest cart to merge"
      ) {
        showToast(mergeResult.message, "success");
      }
    }

    await fetchWishlistFromAPI();
    window.dispatchEvent(new Event("cartUpdated"));
    window.dispatchEvent(new Event("wishlistUpdated"));
    showToast("Login successful!", "success");
  }, []);

  // ── Effects ──
  useEffect(() => {
    return () => {
      if (window.couponInterval) clearInterval(window.couponInterval);
    };
  }, []);

  useEffect(() => {
    fetchAvailableCoupons();
  }, []);

  useEffect(() => {
    if (id) {
      if (isAuthenticated()) fetchWishlistFromAPI();
      fetchReviews();
    }
  }, [id]);

  useEffect(() => {
    if (id) dispatch(fetchProductDetail(id));
    return () => dispatch(clearProductDetail());
  }, [dispatch, id]);

  useEffect(() => {
    if (!product) return;
    if (product.images?.length > 0) {
      const m = product.images.find((i) => i.is_main) || product.images[0];
      setMainImage(m.image || m.image_url);
    }
    const coreData = (product.cores || [])
      .filter((i) => i.title?.trim())
      .sort((a, b) => a.order - b.order)
      .map((i) => ({
        id: i.id,
        title: i.title.trim(),
        subtitle: i.subtitle?.trim() || "",
        image: i.image || i.image_url || null,
      }));
    setCoreItems(coreData);
    const hwdi =
      product.how_we_are_different_image_url ||
      product.how_we_are_different_image;
    if (hwdi) setHowWeAreDifferentImage(hwdi);
    if (product.reviews?.length > 0) {
      const approved = product.reviews.filter(
        (r) => r.status === "approved" || r.status === 1,
      );
      setReviews(approved);
    }
    if (product.average_rating) setAverageRating(product.average_rating);
    if (product.total_reviews) setTotalReviews(product.total_reviews);
  }, [product]);

  useEffect(() => {
    if (mainImage) {
      setSelectedImage(mainImage);
      setSelectedIndex(0);
    }
  }, [mainImage]);

  useEffect(() => {
    if (product?.images?.length > 0) {
      const mainImg =
        product.images.find((i) => i.is_main) || product.images[0];
      if (mainImg) setMainImage(mainImg.image || mainImg.image_url);
    }
  }, [product]);

  const toggleAccordion = (i) => setActiveIndex(activeIndex === i ? null : i);
  const toggleSection = (i) => {
    if (activeSection === i) {
      setActiveSection(null);
      setOpenIndex(null);
    } else {
      setActiveSection(i);
      setOpenIndex(null);
    }
  };

  // ── Guard states ──
  if (status === "loading")
    return (
      <>
        <Navbar />
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
        <Footer />
      </>
    );
  if (status === "failed")
    return (
      <>
        <Navbar />
        <div className="text-center py-5">
          <div className="alert alert-danger">Error: {error}</div>
        </div>
        <Footer />
      </>
    );
  if (!product)
    return (
      <>
        <Navbar />
        <div className="text-center py-5">
          <h4>Product not found</h4>
        </div>
        <Footer />
      </>
    );

  const outOfStock = isOutOfStock();

  // ── Derived values ──
  const productName = product.name || null;
  const productSellingPrice = product.selling_price || null;
  const productPrice = product.price || null;
  const productDiscount = product.discount || null;
  const productWeight = product.weight_value
    ? `${product.weight_value} ${product.weight_unit || "kg"}`
    : null;
  const productShelfLife = product.shelf_life
    ? `${product.shelf_life} months`
    : null;
  const productBarcode = product.sku || null;
  const productIngredients = product.ingredients || null;
  const discountPercent =
    productPrice && productSellingPrice
      ? (((productPrice - productSellingPrice) / productPrice) * 100).toFixed(0)
      : 0;

  // ── cleanVariantName - INSIDE component so it can access productName ──
  const cleanVariantName = (variant) => {
    if (!variant) return productName;
    const flavour = variant.flavour?.trim();
    const weight =
      variant.weight_display ||
      (variant.weight_value
        ? `${variant.weight_value}${variant.weight_unit}`
        : "");
    if (!flavour || flavour === "Default") {
      return weight || productName;
    }
    return weight ? `${flavour} - ${weight}` : flavour;
  };

  // Get default variant batch number
  const defaultVariant =
    product.variants?.find(
      (v) =>
        v.flavour === "Default" || (v.is_active && v.flavour === "Default"),
    ) ||
    product.variants?.find((v) => v.is_active) ||
    null;

  const batchNo = defaultVariant?.batch_no || null;

  const productData = {};
  if (productName) productData["Product Name"] = productName;
  if (productIngredients) productData["Ingredient"] = productIngredients;
  if (productPrice)
    productData["MRP"] = `₹${productPrice} (incl. of all taxes)`;
  if (productBarcode) productData["SKU"] = productBarcode;
  if (productWeight) productData["Weight"] = productWeight;
  if (productShelfLife) productData["Shelf Life"] = productShelfLife;
  productData["Country Of Origin"] = "India";
  if (product.manufacturer_fssai)
    productData["Manufacturer Details With FSSAI Logo & License No."] =
      product.manufacturer_fssai;
  if (product.marketer_fssai)
    productData["Marketer Details With FSSAI Logo & License No."] =
      product.marketer_fssai;

  const displayFeatures = (product.features || [])
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((f, idx) => ({
      id: f.id,
      image: f.image_url || f.image,
      title: f.title,
      order: f.order,
    }));

  // ── uniqueFlavours: normalized dedup to prevent duplicates ──
  const uniqueFlavours = product.variants
    ? [
        ...new Map(
          product.variants.map((v) => [
            v.flavour?.trim().toLowerCase(),
            { ...v, flavour: v.flavour?.trim() },
          ]),
        ).values(),
      ]
    : [];

  const variantsForFlavour = selectedFlavour
    ? (product.variants || []).filter((v) => v.flavour === selectedFlavour)
    : [];

  const displayOverviews = (product.overviews || [])
    .slice()
    .sort((a, b) => a.order - b.order);

  const dynamicFaqs = [];
  if (product.faqs && Array.isArray(product.faqs) && product.faqs.length > 0) {
    product.faqs.forEach((faq) => {
      if (faq.question && faq.answer)
        dynamicFaqs.push({
          question: faq.question,
          answer: faq.answer,
          id: faq.id,
        });
    });
  } else {
    for (let i = 1; i <= 10; i++) {
      if (product[`question${i}`] && product[`answer${i}`])
        dynamicFaqs.push({
          question: product[`question${i}`],
          answer: product[`answer${i}`],
        });
    }
  }

  const dynamicMyths = [];
  for (let i = 1; i <= 4; i++) {
    if (product[`myth_title${i}`] && product[`myth_desc${i}`])
      dynamicMyths.push({
        title: product[`myth_title${i}`],
        desc: product[`myth_desc${i}`],
      });
  }

  const useItems = [];
  for (let i = 1; i <= 5; i++) {
    if (product[`use_title${i}`] && product[`use_desc${i}`])
      useItems.push({
        title: product[`use_title${i}`],
        desc: product[`use_desc${i}`],
      });
  }

  const nutritionalInfoUrl =
    product.nutritional_information_url || product.nutritional_information;
  const labReportUrl = product.lab_report_url || product.lab_report;

  return (
    <>
      <Navbar />

      {showWarning && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            zIndex: 9999,
            backgroundColor:
              warningMessage.includes("added") ||
              warningMessage.includes("removed")
                ? "#4CAF50"
                : "#ff4444",
            color: "white",
            padding: "15px 25px",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            animation: "slideIn 0.3s ease",
          }}
        >
          <i
            className={`fa-${warningMessage.includes("added") || warningMessage.includes("removed") ? "regular fa-circle-check" : "solid fa-circle-exclamation"}`}
          ></i>
          <span>{warningMessage}</span>
          <button
            onClick={closeWarning}
            style={{
              background: "none",
              border: "none",
              color: "white",
              marginLeft: "10px",
              cursor: "pointer",
              fontSize: "18px",
            }}
          >
            ×
          </button>
        </div>
      )}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={hideToast} />
      )}

      {/* Login Popup Modal */}
      {showLoginPopup && (
        <div
          className="login-popup-overlay"
          onClick={() => setShowLoginPopup(false)}
        >
          <div
            className="login-popup-container"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="login-popup-close"
              onClick={() => setShowLoginPopup(false)}
            >
              <i className="fa-solid fa-times"></i>
            </button>
            <Login
              setOpen={setShowLoginPopup}
              onLoginSuccess={handleLoginSuccess}
            />
          </div>
        </div>
      )}

      {productName && (
        <section className="product-section">
          <div className="container-fluid-lg">
            <div className="row">
              <div className="col-xxl-12 col-xl-12 col-lg-12 wow fadeInUp">
                <div className="row g-4">
                  {mainImage && (
                    <div className="col-xl-6 wow fadeInUp">
                      <div className="product-left-box">
                        <div className="row g-sm-4 g-2">
                          <div className="col-12">
                            <div className="product-main-image mb-3">
                              <div className="slider-image">
                                <img
                                  src={getImageUrl(selectedImage)}
                                  className="img-fluid blur-up lazyload"
                                  alt={product.name}
                                  style={{
                                    width: "100%",
                                    cursor: "pointer",
                                    borderRadius: "8px",
                                    transition: "transform 0.3s ease",
                                  }}
                                />
                              </div>
                            </div>
                            {allImages.length > 1 && (
                              <div className="product-thumbnails">
                                <div
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "6px",
                                  }}
                                >
                                  {allImages.map((img, idx) => (
                                    <div
                                      key={img.id}
                                      className={`thumbnail-image ${selectedIndex === idx ? "active" : ""}`}
                                      onClick={() => {
                                        setSelectedImage(img.image);
                                        setSelectedIndex(idx);
                                      }}
                                      style={{
                                        cursor: "pointer",
                                        width: "80px",
                                        height: "80px",
                                        border:
                                          selectedIndex === idx
                                            ? "2px solid #007bff"
                                            : "1px solid #ddd",
                                        borderRadius: "4px",
                                        overflow: "hidden",
                                        backgroundColor: "#f9f9f9",
                                        transition: "border 0.3s ease",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                      }}
                                    >
                                      <img
                                        src={getImageUrl(img.image)}
                                        alt={img.alt}
                                        className="blur-up lazyload"
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "contain",
                                          padding: "3px",
                                        }}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* RIGHT — details */}
                  <div className="col-xl-6 wow fadeInUp">
                    <div className="right-box-contain">
                      <div className="d-flex justify-content-between align-items-start">
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            flexWrap: "wrap",
                          }}
                        >
                          {outOfStock && (
                            <span
                              style={{
                                backgroundColor: "#ff4444",
                                color: "white",
                                padding: "4px 12px",
                                borderRadius: "20px",
                                fontSize: "12px",
                                fontWeight: "600",
                                display: "inline-block",
                              }}
                            >
                              <FaTimesCircle
                                style={{ marginRight: "5px", fontSize: "11px" }}
                              />{" "}
                              OUT OF STOCK
                            </span>
                          )}

                          {(() => {
                            const showBestseller =
                              selectedVariant?.is_bestseller === true ||
                              product?.is_bestseller === true;
                            return (
                              showBestseller && (
                                <span
                                  style={{
                                    background: "#ff9800",
                                    color: "white",
                                    padding: "4px 12px",
                                    borderRadius: "20px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "5px",
                                    fontSize: "11px",
                                    fontWeight: "700",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="gold"
                                    stroke="gold"
                                    strokeWidth="0"
                                  >
                                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                  </svg>
                                  BESTSELLER
                                </span>
                              )
                            );
                          })()}

                          {(() => {
                            const showMostPopular =
                              (selectedVariant?.is_most_popular === true ||
                                product?.is_most_popular === true) &&
                              !(
                                selectedVariant?.is_bestseller === true ||
                                product?.is_bestseller === true
                              );
                            return (
                              showMostPopular && (
                                <span
                                  style={{
                                    background: "#9b59b6",
                                    color: "white",
                                    padding: "4px 12px",
                                    borderRadius: "20px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "5px",
                                    fontSize: "11px",
                                    fontWeight: "700",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  <span style={{ fontSize: "12px" }}>🔥</span>
                                  MOST POPULAR
                                </span>
                              )
                            );
                          })()}
                        </div>

                        <div style={{ display: "flex", gap: "10px" }}>
                          <button
                            onClick={() => toggleWishlist(product.id)}
                            disabled={wishlistLoading[product.id]}
                            style={{
                              background: "none",
                              border: "none",
                              fontSize: "24px",
                              cursor: wishlistLoading[product.id]
                                ? "wait"
                                : "pointer",
                              color: isInWishlist(product.id)
                                ? "#ff4444"
                                : "#999",
                              transition: "color 0.3s ease,transform 0.2s ease",
                              opacity: wishlistLoading[product.id] ? 0.7 : 1,
                            }}
                            onMouseEnter={(e) => {
                              if (!wishlistLoading[product.id])
                                e.currentTarget.style.transform = "scale(1.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "scale(1)";
                            }}
                          >
                            {wishlistLoading[product.id] ? (
                              <span
                                className="spinner-border spinner-border-sm"
                                style={{ width: "20px", height: "20px" }}
                              ></span>
                            ) : isInWishlist(product.id) ? (
                              <FaHeart />
                            ) : (
                              <FaRegHeart />
                            )}
                          </button>
                          <button
                            onClick={() => shareProduct(product)}
                            style={{
                              background: "none",
                              border: "none",
                              fontSize: "24px",
                              cursor: "pointer",
                              color: "#999",
                              transition: "transform 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "scale(1.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "scale(1)";
                            }}
                          >
                            <i className="fa fa-share-alt"></i>
                          </button>
                        </div>
                      </div>

                      <h2 className="name" style={{ fontSize: "20px" }}>
                        {productName}
                      </h2>

                      <div className="price-rating">
                        <div style={{ marginBottom: "8px" }}>
                          {(selectedVariant || productSellingPrice) && (
                            <h3
                              className="theme-color price"
                              style={{
                                marginBottom: "0px",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                flexWrap: "wrap",
                              }}
                            >
                              <span
                                style={{
                                  fontWeight: "700",
                                  fontSize: "24px",
                                  color: "#000000",
                                }}
                              >
                                ₹
                                {selectedVariant
                                  ? selectedVariant.price
                                  : productSellingPrice}
                              </span>
                              {(() => {
                                const sellingPrice = selectedVariant
                                  ? parseFloat(selectedVariant.price)
                                  : parseFloat(productSellingPrice);
                                const mrpPrice = selectedVariant
                                  ? parseFloat(selectedVariant.mrp)
                                  : parseFloat(productPrice);
                                const discPct =
                                  mrpPrice > sellingPrice
                                    ? (
                                        ((mrpPrice - sellingPrice) / mrpPrice) *
                                        100
                                      ).toFixed(0)
                                    : 0;
                                return mrpPrice > sellingPrice ? (
                                  <>
                                    <s
                                      style={{
                                        fontSize: "0.9rem",
                                        color: "#999",
                                        textDecoration: "line-through",
                                      }}
                                    >
                                      ₹{mrpPrice}
                                    </s>
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "3px",
                                        fontSize: "0.75rem",
                                        color: "#fff",
                                        backgroundColor: "#e53935",
                                        padding: "4px 10px",
                                        borderRadius: "20px",
                                        fontWeight: "700",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {discPct}% OFF
                                    </span>
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        fontSize: "0.75rem",
                                        color: "#2e7d32",
                                        backgroundColor: "#e8f5e9",
                                        padding: "4px 10px",
                                        borderRadius: "20px",
                                        fontWeight: "700",
                                        border: "1px solid #a5d6a7",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="12"
                                        height="12"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="#2e7d32"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                                        <line x1="7" y1="7" x2="7.01" y2="7" />
                                      </svg>
                                      Save ₹
                                      {(mrpPrice - sellingPrice).toLocaleString(
                                        "en-IN",
                                      )}
                                    </span>
                                  </>
                                ) : null;
                              })()}
                            </h3>
                          )}
                        </div>
                        <div className="product-rating custom-rate">
                          <ul className="rating">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <li key={s}>
                                <i
                                  className={
                                    s <= averageRating
                                      ? "fa-solid fa-star"
                                      : "fa-regular fa-star"
                                  }
                                  style={{
                                    color:
                                      s <= averageRating ? "#FFD700" : "#ddd",
                                  }}
                                ></i>
                              </li>
                            ))}
                          </ul>
                          {(totalReviews > 0 || reviews.length > 0) && (
                            <span className="review">
                              {totalReviews || reviews.length} Customer Reviews
                            </span>
                          )}
                          {averageRating > 0 && (
                            <span className="ms-2 text-muted">
                              ({Number(averageRating).toFixed(1)}/5)
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Promo banner */}
                      <div style={{ width: "100%", marginTop: "16px" }}>
                        {couponsLoading ? (
                          <div
                            style={{
                              width: "100%",
                              height: "70px",
                              background: "#FFD35A",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: "8px",
                            }}
                          >
                            <span
                              style={{ color: "#2a2a2a", fontWeight: "600" }}
                            >
                              Loading offers...
                            </span>
                          </div>
                        ) : availableCoupons.length > 0 ? (
                          <div
                            style={{
                              width: "100%",
                              background: "#FFD35A",
                              borderRadius: "12px",
                              overflow: "hidden",
                              position: "relative",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: window.innerWidth <= 768 ? "10px" : "12px",
                                padding:
                                  window.innerWidth <= 480
                                    ? "8px 10px"
                                    : window.innerWidth <= 768
                                      ? "10px 12px"
                                      : "12px 16px",
                                position: "relative",
                                zIndex: 1,
                              }}
                            >
                              <span
                                style={{
                                  fontSize:
                                    window.innerWidth <= 480
                                      ? "20px"
                                      : window.innerWidth <= 768
                                        ? "24px"
                                        : "28px",
                                  flexShrink: 0,
                                }}
                              >
                                ⚡
                              </span>

                              <div style={{ flex: 1, minWidth: 0 }}>
                                {/* Coupon Code */}
                                <div
                                  style={{
                                    fontSize:
                                      window.innerWidth <= 480
                                        ? "12px"
                                        : window.innerWidth <= 768
                                          ? "13px"
                                          : "16px",
                                    fontWeight: "700",
                                    color: "#2a2a2a",
                                    marginBottom: "4px",
                                  }}
                                >
                                  {availableCoupons[currentPromoIndex]?.code ||
                                    "Use code:"}
                                </div>

                                {/* Description with Read More */}
                                <div
                                  style={{
                                    fontSize:
                                      window.innerWidth <= 480
                                        ? "11px"
                                        : window.innerWidth <= 768
                                          ? "12px"
                                          : "13px",
                                    color: "#2a2a2a",
                                    lineHeight: "1.5",
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {(() => {
                                    const desc =
                                      availableCoupons[currentPromoIndex]
                                        ?.description ||
                                      (availableCoupons[currentPromoIndex]
                                        ?.discount_type === "percentage"
                                        ? `${availableCoupons[currentPromoIndex]?.discount_value}% OFF`
                                        : `₹${availableCoupons[currentPromoIndex]?.discount_value} OFF`);

                                    if (!desc) return null;

                                    const isLong = desc.length > 50;

                                    return (
                                      <>
                                        {showFullDesc
                                          ? desc
                                          : desc.substring(0, 50) +
                                            (isLong ? "..." : "")}

                                        {isLong && (
                                          <span
                                            onClick={() =>
                                              setShowFullDesc(!showFullDesc)
                                            }
                                            style={{
                                              color: "#000",
                                              fontWeight: "600",
                                              marginLeft: "6px",
                                              cursor: "pointer",
                                            }}
                                          >
                                            {showFullDesc
                                              ? "Read Less"
                                              : "Read More"}
                                          </span>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>

                              {/* Copy Button */}
                              <button
                                onClick={() =>
                                  copyToClipboard(
                                    availableCoupons[currentPromoIndex]?.code,
                                  )
                                }
                                style={{
                                  background:
                                    copiedCoupon ===
                                    availableCoupons[currentPromoIndex]?.code
                                      ? "#28a745"
                                      : "#000",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "25px",
                                  padding:
                                    window.innerWidth <= 480
                                      ? "5px 10px"
                                      : window.innerWidth <= 768
                                        ? "6px 12px"
                                        : "8px 16px",
                                  fontSize:
                                    window.innerWidth <= 480
                                      ? "10px"
                                      : window.innerWidth <= 768
                                        ? "11px"
                                        : "12px",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  whiteSpace: "nowrap",
                                  flexShrink: 0,
                                }}
                              >
                                {copiedCoupon ===
                                availableCoupons[currentPromoIndex]?.code ? (
                                  <>
                                    <i className="fa-regular fa-circle-check"></i>{" "}
                                    Copied!
                                  </>
                                ) : (
                                  <>
                                    <i className="fa-regular fa-copy"></i> Copy
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div
                            style={{
                              width: "100%",
                              background: "#FFD35A",
                              borderRadius: "12px",
                              overflow: "hidden",
                            }}
                          >
                            {/* <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                padding: "12px 16px",
                              }}
                            >
                              <span style={{ fontSize: "24px" }}>⚡</span>

                              <div>
                                <div
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "700",
                                    color: "#2a2a2a",
                                  }}
                                >
                                  Use code: SAVE20
                                </div>
                                <div
                                  style={{ fontSize: "12px", color: "#2a2a2a" }}
                                >
                                  Get Flat 20% OFF on all orders
                                </div>
                              </div>
                            </div> */}
                          </div>
                        )}
                      </div>

<div className="amazon-card">
  <div className="amazon-left">
    <p className="amazon-title">Available on</p>

    <a
       href={product?.amazon_link}
      target="_blank"
      rel="noopener noreferrer"
    >
      <img
        src="https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg"
        alt="Amazon"
        className="amazon-logo"
      />
    </a>
  </div>

  <div className="amazon-middle">
    <div className="feature">
      <span>🟠</span>
      <span>Genuine Product</span>
    </div>

    <div className="feature">
      <span>🟠</span>
      <span>Fast Delivery</span>
    </div>

    <div className="feature">
      <span>🟠</span>
      <span>Easy Returns</span>
    </div>
  </div>

  <div className="amazon-right">
    <a
      href={product?.amazon_link}
      target="_blank"
      rel="noopener noreferrer"
      className="amazon-btn"
    >
      View on Amazon 
    </a>
  </div>
</div>
                      {/* ── Variant Selector ── */}
                      {(product.variants || []).length > 0 && (
                        <div className="product-package mt-3">
                          <div className="product-title">
                            <h4>Select Pack Size:</h4>
                          </div>
                          <div className="variants-container">
                            {(product.variants || []).map((variant) => {
                              const vOut = variantIsOut(variant);
                              const isSelected =
                                selectedVariant?.id === variant.id;

                              // Build display name directly from clean API fields
                              let displayName;
                              if (variant.flavour === "Default") {
                                displayName =
                                  variant.weight_display ||
                                  `${variant.weight_value}${variant.weight_unit}`;
                              } else {
                                const weight =
                                  variant.weight_display ||
                                  (variant.weight_value
                                    ? `${variant.weight_value}${variant.weight_unit}`
                                    : "");
                                displayName = weight
                                  ? `${variant.flavour} (${weight})`
                                  : variant.flavour;
                              }

                              return (
                                <div
                                  key={variant.id}
                                  className="flavour-card"
                                  // onClick={() => {
                                  //   if (vOut) return;
                                  //   setSelectedVariant(variant);
                                  //   setSelectedFlavour(variant.flavour);
                                  // }}

                                  onClick={() => {
                                    if (vOut) return;

                                    setSelectedVariant(variant);
                                    setSelectedFlavour(variant.flavour);

                                    // ✅ MAIN FIX
                                    const img =
                                      variant.main_image || variant.image_url;
                                    if (img) {
                                      setMainImage(img);
                                      setSelectedImage(img);
                                      setSelectedIndex(0); // optional reset
                                    }
                                  }}
                                  style={{
                                    opacity: vOut ? 0.6 : 1,
                                    cursor: vOut ? "not-allowed" : "pointer",
                                    backgroundColor: isSelected
                                      ? "#9b59b6"
                                      : "#fff",
                                    color: isSelected ? "#ffffff" : "#000000",
                                    border: isSelected
                                      ? "2px solid #9b59b6"
                                      : "1px solid #e0e0e0",
                                    borderRadius: "8px",
                                    padding: "12px",
                                    position: "relative",
                                    transition: "all 0.2s ease",
                                  }}
                                  onMouseEnter={(e) => {
                                    if (vOut || isSelected) return;
                                    e.currentTarget.style.backgroundColor =
                                      "#9b59b6";
                                    e.currentTarget.style.color = "#ffffff";
                                    const priceSpan =
                                      e.currentTarget.querySelector(
                                        ".price-span",
                                      );
                                    const mrpDel =
                                      e.currentTarget.querySelector(".mrp-del");
                                    const packText =
                                      e.currentTarget.querySelector(
                                        ".pack-size-text",
                                      );
                                    if (priceSpan)
                                      priceSpan.style.color = "#ffffff";
                                    if (mrpDel) mrpDel.style.color = "#e0e0e0";
                                    if (packText)
                                      packText.style.color = "#ffffff";
                                  }}
                                  onMouseLeave={(e) => {
                                    if (vOut || isSelected) return;
                                    e.currentTarget.style.backgroundColor =
                                      "#fff";
                                    e.currentTarget.style.color = "#000000";
                                    const priceSpan =
                                      e.currentTarget.querySelector(
                                        ".price-span",
                                      );
                                    const mrpDel =
                                      e.currentTarget.querySelector(".mrp-del");
                                    const packText =
                                      e.currentTarget.querySelector(
                                        ".pack-size-text",
                                      );
                                    if (priceSpan)
                                      priceSpan.style.color = "#000000";
                                    if (mrpDel) mrpDel.style.color = "#999999";
                                    if (packText)
                                      packText.style.color = "#666666";
                                  }}
                                >
                                  {isSelected && (
                                    <div
                                      className="checkmark-badge"
                                      style={{
                                        position: "absolute",
                                        top: "6px",
                                        right: "6px",
                                        width: "18px",
                                        height: "18px",
                                        borderRadius: "50%",
                                        backgroundColor: "#ffffff",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        zIndex: 1,
                                      }}
                                    >
                                      <i
                                        className="fa fa-check"
                                        style={{
                                          color: "#9b59b6",
                                          fontSize: "10px",
                                        }}
                                      ></i>
                                    </div>
                                  )}

                                  <div
                                    className="img-wrapper"
                                    style={{
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      marginBottom: "10px",
                                    }}
                                  >
                                    <img
                                      src={
                                        variant.main_image ||
                                        variant.image_url ||
                                        "/assets/images/new-images/Family-Poster-1.png"
                                      }
                                      alt={displayName}
                                      style={{
                                        height: "80px",
                                        width: "auto",
                                        maxWidth: "100%",
                                        objectFit: "contain",
                                      }}
                                      onError={(e) => {
                                        e.target.src =
                                          "/assets/images/new-images/Family-Poster-1.png";
                                      }}
                                    />
                                  </div>

                                  <p
                                    className="pack-size-text"
                                    style={{
                                      fontSize: "13px",
                                      color: isSelected ? "#ffffff" : "#333333",
                                      fontWeight: "500",
                                      margin: "0 0 4px 0",
                                      textAlign: "center",
                                    }}
                                  >
                                    {displayName}
                                  </p>

                                  <div style={{ textAlign: "center" }}>
                                    <span
                                      className="price-span"
                                      style={{
                                        fontWeight: "700",
                                        fontSize: "14px",
                                        color: isSelected
                                          ? "#ffffff"
                                          : "#000000",
                                        transition: "color 0.2s ease",
                                      }}
                                    >
                                      ₹
                                      {Math.round(variant.price).toLocaleString(
                                        "en-IN",
                                      )}
                                    </span>
                                    {variant.mrp > variant.price && (
                                      <del
                                        className="mrp-del"
                                        style={{
                                          fontSize: "11px",
                                          color: isSelected
                                            ? "#e0e0e0"
                                            : "#999999",
                                          marginLeft: "6px",
                                          transition: "color 0.2s ease",
                                        }}
                                      >
                                        ₹
                                        {Math.round(variant.mrp).toLocaleString(
                                          "en-IN",
                                        )}
                                      </del>
                                    )}
                                    {variant.serving_size && (
                                      <div
                                        style={{
                                          fontSize: "10px",
                                          color: isSelected
                                            ? "#e0e0e0"
                                            : "#888888",
                                          marginTop: "5px",
                                          fontWeight: "500",
                                          letterSpacing: "0.3px",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          gap: "4px",
                                        }}
                                      >
                                        <i
                                          className="fa-solid fa-utensil-spoon"
                                          style={{ fontSize: "9px" }}
                                        ></i>
                                        <span>{variant.serving_size}</span>
                                      </div>
                                    )}
                                  </div>

                                  {vOut && (
                                    <small
                                      style={{
                                        color: "#ff4444",
                                        fontSize: "11px",
                                        marginTop: "4px",
                                        display: "block",
                                        textAlign: "center",
                                      }}
                                    >
                                      Out of Stock
                                    </small>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Features */}
                      {displayFeatures.length > 0 && (
                        <div className="features-container">
                          <div className="features-grid">
                            {displayFeatures.map((f, i) => (
                              <div className="feature-item" key={f.id || i}>
                                <div className="icon">
                                  {f.image ? (
                                    <img
                                      src={getImageUrl(f.image)}
                                      alt={f.title}
                                      style={{
                                        width: "34px",
                                        height: "34px",
                                        objectFit: "contain",
                                      }}
                                    />
                                  ) : (
                                    FEATURE_ICONS[i % FEATURE_ICONS.length]
                                  )}
                                </div>
                                <p>{f.title}</p>
                              </div>
                            ))}
                          </div>
                          {product.feature_description && (
                            <p className="description">
                              {isExpanded
                                ? product.feature_description
                                : product.short_description ||
                                  product.feature_description.substring(
                                    0,
                                    150,
                                  ) + "..."}
                              {product.feature_description.length > 150 && (
                                <span
                                  className="read-more"
                                  onClick={() => setIsExpanded(!isExpanded)}
                                >
                                  {isExpanded ? " Read less" : " Read more"}
                                </span>
                              )}
                            </p>
                          )}
                          <div className="logo-row">
                            <img
                              src="/assets/images/Frame_1.svg"
                              alt="certification"
                              className="cert-logo"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <FrequentlyBoughtBundles
        bundles={product.frequently_bought_bundles}
        isAuthenticated={isAuthenticated}
        getCustomerId={getCustomerId}
        showWarningMessage={(msg) => {
          setWarningMessage(msg);
          setShowWarning(true);
          setTimeout(() => setShowWarning(false), 3000);
        }}
      />

      {/* Five Ways to Use It */}
      {useItems.length > 0 && (
        <section className="use-section">
          <div className="container">
            <h2 className="use-heading">Five Ways to Use It</h2>
            <div className="use-grid">
              {useItems.map((item, idx) => (
                <div className="use-card" key={idx}>
                  <i className="fa-solid fa-dumbbell use-icon"></i>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* YP Protein / Overviews */}
      {displayOverviews.length > 0 && (
        <section
          className="yp-protein-section"
        >
          <div className="yp-protein-container">
            <div className="yp-protein-image">
              <img src={product.overview_image} alt="Fermented Yeast Protein" />
            </div>
            <div className="yp-protein-content">
              <h2 className="yp-protein-headline">{product.overview_title}</h2>
              <div className="yp-benefits-grid">
                {displayOverviews.map((item) => (
                  <div className="yp-benefit-item" key={item.id}>
                    <strong>{item.subtitle}</strong>
                    <p>{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* How We Are Different */}
      {howWeAreDifferentImage && (
        <section className="after-core-image">
          <div className="container">
            <div className="image-wrapper">
              <img
                src={getImageUrl(howWeAreDifferentImage)}
                alt="How We Are Different"
                loading="lazy"
              />
            </div>
          </div>
        </section>
      )}

      {/* The Core Within */}
      {coreItems.length > 0 && (
        <section
          className="core-with"
          style={{ padding: "60px 20px", background: "#fff" }}
        >
          <div
            className="container"
            style={{ maxWidth: "1200px", margin: "0 auto" }}
          >
            <h2
              className="title"
              style={{
                fontSize: "32px",
                fontWeight: "700",
                textAlign: "center",
                marginBottom: "40px",
                color: "#333",
                position: "relative",
              }}
            >
              The Core Within
            </h2>
            <div
              className="ingredient-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                gap: "30px",
                justifyItems: "center",
              }}
            >
              {coreItems.map((item) => (
                <div
                  key={item.id}
                  className="ingredient-card"
                  style={{
                    background: "#fff",
                    borderRadius: "12px",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  }}
                >
                  <div
                    className="image-wrapper"
                    style={{
                      height: "200px",
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#f8f8f8",
                      borderRadius: "8px",
                      marginBottom: "15px",
                      overflow: "hidden",
                      padding: "15px",
                    }}
                  >
                    {item.image ? (
                      <img
                        src={
                          getImageUrl(item.image) ||
                          "/assets/images/Family-Poster-2.png"
                        }
                        alt={item.title}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          maxWidth: "180px",
                          maxHeight: "180px",
                          transition: "transform 0.3s ease",
                        }}
                      />
                    ) : (
                      <img
                        src="/assets/images/Family-Poster-2.png"
                        alt={item.title}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          maxWidth: "180px",
                          maxHeight: "180px",
                          transition: "transform 0.3s ease",
                        }}
                      />
                    )}
                  </div>
                  <div
                    className="content"
                    style={{ textAlign: "center", padding: "0 12px 20px 12px" }}
                  >
                    <h4
                      className="name"
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        marginBottom: item.subtitle ? "5px" : "0",
                        minHeight: item.subtitle ? "40px" : "20px",
                        color: "#333",
                        lineHeight: "1.3",
                      }}
                    >
                      {item.title}
                    </h4>
                    {item.subtitle && (
                      <span
                        className="sub"
                        style={{
                          fontSize: "14px",
                          color: "#666",
                          display: "block",
                          lineHeight: "1.4",
                        }}
                      >
                        {item.subtitle}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Flavours showcase */}
      {uniqueFlavours.length > 0 && (
        <section className="flavours-new">
          <div className="container">
            <h2 className="flavours-main-title">
              <span>delicious flavours</span>, one goal
            </h2>
            <div className="flavours-center">
              {uniqueFlavours.map((v) => (
                <div
                  key={v.flavour}
                  className={`flavour-card-lg ${selectedFlavour === v.flavour ? "active" : ""}`}
                  onClick={() => setSelectedFlavour(v.flavour)}
                  style={{ cursor: "pointer" }}
                >
                  <img
                    src={
                      getImageUrl(v.main_image) ||
                      "/assets/images/new-images/Family-Poster-1.png"
                    }
                    alt={v.flavour}
                  />
                  <p>{v.flavour}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Collapsible info */}
      <section className="collapsible-section">
        <div className="container">
          {nutritionalInfoUrl && (
            <div
              className={`accordion-item ${activeIndex === 0 ? "active" : ""}`}
            >
              <button
                className="accordion-header"
                onClick={() => toggleAccordion(0)}
              >
                <span>Nutritional information</span>
                <span className="icon">{activeIndex === 0 ? "−" : "+"}</span>
              </button>
              {activeIndex === 0 && (
                <div className="accordion-content">
                  <div className="row justify-content-center">
                    <div className="col-12 col-md-10 col-lg-8">
                      <img
                        src={getImageUrl(nutritionalInfoUrl)}
                        alt="Nutritional Information"
                        className="img-fluid rounded"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {productIngredients && (
            <div
              className={`accordion-item ${activeIndex === 1 ? "active" : ""}`}
            >
              <button
                className="accordion-header"
                onClick={() => toggleAccordion(1)}
              >
                <span>Ingredients</span>
                <span className="icon">{activeIndex === 1 ? "−" : "+"}</span>
              </button>
              {activeIndex === 1 && (
                <div className="accordion-content">
                  <div className="ingredients-content">
                    <p>{productIngredients}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {product.description && (
            <div
              className={`accordion-item ${activeIndex === 2 ? "active" : ""}`}
            >
              <button
                className="accordion-header"
                onClick={() => toggleAccordion(2)}
              >
                <span>Why choose Evercore Fermented Yeast Protein?</span>
                <span className="icon">{activeIndex === 2 ? "−" : "+"}</span>
              </button>
              {activeIndex === 2 && (
                <div className="accordion-content">
                  <div className="ingredients-content">
                    <p>{product.description}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {useItems.length > 0 && (
            <div
              className={`accordion-item ${activeIndex === 3 ? "active" : ""}`}
            >
              <button
                className="accordion-header"
                onClick={() => toggleAccordion(3)}
              >
                <span>{product.question1}</span>
                <span className="icon">{activeIndex === 3 ? "−" : "+"}</span>
              </button>
              {activeIndex === 3 && (
                <div className="accordion-content">
                  <div className="ingredients-content">
                    <p>{product.answer1}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {useItems.length > 0 && (
            <div
              className={`accordion-item ${activeIndex === 4 ? "active" : ""}`}
            >
              <button
                className="accordion-header"
                onClick={() => toggleAccordion(4)}
              >
                <span>{product.question2}</span>
                <span className="icon">{activeIndex === 4 ? "−" : "+"}</span>
              </button>
              {activeIndex === 4 && (
                <div className="accordion-content">
                  <div className="ingredients-content">
                    <p>{product.answer2}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {dynamicMyths.length > 0 && (
            <div
              className={`accordion-item ${activeIndex === 5 ? "active" : ""}`}
            >
              <button
                className="accordion-header"
                onClick={() => toggleAccordion(5)}
              >
                <span>Myths About Fermented Yeast Protein</span>
                <span className="icon">{activeIndex === 5 ? "−" : "+"}</span>
              </button>
              {activeIndex === 5 && (
                <div className="accordion-content">
                  <div className="ingredients-content">
                    {dynamicMyths.map((myth, i) => (
                      <div className="card-content" key={i}>
                        <p style={{ textAlign: "center", fontSize: "14px" }}>
                          <strong>{myth.title}</strong>
                        </p>
                        <p style={{ textAlign: "justify" }}>{myth.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Evercore Standard */}
      <section className="evercore-standard">
        <div className="container-fluid-lg">
          <div className="standard-container">
            <span className="standard-eyebrow">THE EVERCORE STANDARD</span>
            <h2>Our Commitment to Quality & Purity</h2>
            <div className="standard-items">
              {[
                ["mdi:leaf", "Monk Fruit Sweetened"],
                ["mdi:flask-outline", "Science-Led Formulation"],
                ["mdi:stomach", "Easy on Digestion"],
                ["mdi:earth", "Globally Sourced Ingredients"],
                ["mdi:water-off", "Preservative Free"],
                ["mdi:run", "Lifestyle Friendly"],
              ].map(([icon, label]) => (
                <div className="standard-item" key={label}>
                  <Icon icon={icon} />
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="certification-section">
        <div className="container-fluid-lg">
          <div className="title mb-4">
            <h2
              className="position-relative d-inline-block pb-3"
              style={{ color: "#000" }}
            >
              Certifications
              <span
                className="position-absolute bottom-0 start-0"
                style={{
                  width: "60px",
                  height: "3px",
                  backgroundColor: "#000000",
                  borderRadius: "2px",
                }}
              />
            </h2>
          </div>
          <div
            className="new-slider-wrapper"
            style={{
              backgroundColor: "#90D5FF",
              padding: "30px 20px",
              borderRadius: "16px",
            }}
          >
            <div className="new-slider-slider">
              {[...certifications, ...certifications].map((item, index) => (
                <div className="cert-item" key={index}>
                  <a href="/Shop" className="cert-card">
                    <img src={item.img} alt={item.alt} />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <div
        className="reviews-container"
        style={{ marginTop: "60px", padding: "0 20px" }}
      >
        <div
          className="reviews-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h2>
            Customer Reviews{" "}
            {(totalReviews > 0 || reviews.length > 0) &&
              `(${totalReviews || reviews.length})`}
            {averageRating > 0 && (
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: "normal",
                  marginLeft: "10px",
                  color: "#666",
                }}
              >
                (Average: {Number(averageRating).toFixed(1)}/5)
              </span>
            )}
          </h2>
          <button
            onClick={() => setShowForm(true)}
            style={{
              padding: "10px 20px",
              backgroundColor: "#000",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            Write a Review
          </button>
        </div>

        {reviewsLoading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <div className="spinner-border text-primary" role="status"></div>
          </div>
        ) : reviewsError ? (
          <div
            style={{ textAlign: "center", padding: "40px", color: "#ff4444" }}
          >
            {reviewsError}
          </div>
        ) : reviews.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
            }}
          >
            <p style={{ color: "#666", fontSize: "16px" }}>
              No reviews yet. Be the first to review this product!
            </p>
          </div>
        ) : (
          reviews.map((review) => (
            <div
              key={review.id}
              style={{
                border: "1px solid #eee",
                borderRadius: "8px",
                padding: "20px",
                marginBottom: "15px",
                backgroundColor: "#fff",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "10px",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <StarRating rating={review.rating} />
                  <span style={{ fontSize: "14px", color: "#666" }}>
                    {review.rating}/5
                  </span>
                </div>
                <span style={{ fontSize: "12px", color: "#888" }}>
                  {review.created_at ? formatDate(review.created_at) : ""}
                </span>
              </div>
              <p style={{ fontWeight: "600", margin: "0 0 10px 0" }}>
                {review.customer_name || "Anonymous"}
              </p>
              <p style={{ margin: 0, lineHeight: "1.6", color: "#333" }}>
                {review.review}
              </p>
            </div>
          ))
        )}
      </div>

      {/* Review modal */}
      {showForm && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%,-50%)",
            backgroundColor: "#fff",
            padding: "30px",
            borderRadius: "12px",
            boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            zIndex: 10000,
            width: "90%",
            maxWidth: "500px",
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <h3
            style={{
              marginBottom: "20px",
              fontSize: "20px",
              fontWeight: "600",
            }}
          >
            Write a Review
          </h3>
          <form onSubmit={handleSubmitReview}>
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "10px",
                  fontWeight: "500",
                }}
              >
                Your Rating *
              </label>
              <InteractiveStarRating
                rating={rating}
                setRating={setRating}
                hoverRating={hoverRating}
                setHoverRating={setHoverRating}
              />
            </div>
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "10px",
                  fontWeight: "500",
                }}
              >
                Your Review *
              </label>
              <textarea
                placeholder="Share your experience with this product..."
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                rows="5"
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "14px",
                  resize: "vertical",
                }}
                required
              />
            </div>
            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
              }}
            >
              <button
                type="button"
                onClick={() => setShowForm(false)}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#f0f0f0",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  submittingReview || !reviewText.trim() || rating === 0
                }
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#000",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor:
                    submittingReview || !reviewText.trim() || rating === 0
                      ? "not-allowed"
                      : "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                  opacity:
                    submittingReview || !reviewText.trim() || rating === 0
                      ? 0.7
                      : 1,
                }}
              >
                {submittingReview ? "Submitting..." : "Submit Review"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Additional collapsibles */}
      <section className="collapsible-section">
        <div className="container">
          {labReportUrl && (
            <div
              className={`accordion-item ${activeSection === 0 ? "active" : ""}`}
            >
              <button
                className="accordion-header"
                onClick={() => {
                  window.open(getImageUrl(labReportUrl), "_blank");
                }}
              >
                <span>Lab Reports</span>
                <span className="icon">🔗</span>
              </button>
            </div>
          )}

          {product.know_more && (
            <div
              className={`accordion-item ${activeSection === 1 ? "active" : ""}`}
            >
              <button
                className="accordion-header"
                onClick={() => toggleSection(1)}
              >
                <span>Know More</span>
                <span className="icon">{activeSection === 1 ? "−" : "+"}</span>
              </button>
              {activeSection === 1 && (
                <div className="accordion-content">
                  <p>{product.know_more}</p>
                </div>
              )}
            </div>
          )}

          {product.faqs &&
            Array.isArray(product.faqs) &&
            product.faqs.length > 0 && (
              <div
                className={`accordion-item ${activeSection === 2 ? "active" : ""}`}
              >
                <button
                  className="accordion-header"
                  onClick={() => {
                    setActiveSection(activeSection === 2 ? null : 2);
                    setActiveFaqIndex(null);
                    setShowFaqs(false);
                  }}
                >
                  <span>FAQs</span>
                  <span className="icon">
                    {activeSection === 2 ? "−" : "+"}
                  </span>
                </button>
                {activeSection === 2 && (
                  <div>
                    <div className="accordion-content scrollable">
                      <div className="container">
                        {product.faqs
                          .slice(0, showFaqs ? product.faqs.length : 3)
                          .map((faq, idx) => (
                            <div
                              key={faq.id || idx}
                              className={`accordion-item ${activeFaqIndex === idx ? "active" : ""}`}
                            >
                              <button
                                className="accordion-header"
                                onClick={() =>
                                  setActiveFaqIndex(
                                    activeFaqIndex === idx ? null : idx,
                                  )
                                }
                              >
                                <span>{faq.question}</span>
                                <span className="icon">
                                  {activeFaqIndex === idx ? "−" : "+"}
                                </span>
                              </button>
                              {activeFaqIndex === idx && (
                                <div
                                  className="accordion-content"
                                  style={{ padding: "8px 16px" }}
                                >
                                  <p>{faq.answer}</p>
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                    {product.faqs.length > 3 && (
                      <p
                        onClick={() => setShowFaqs(!showFaqs)}
                        style={{
                          color: "blue",
                          cursor: "pointer",
                          marginTop: "12px",
                          textAlign: "center",
                        }}
                      >
                        {showFaqs ? "Read Less" : "Read More"}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

          {Object.keys(productData).length > 0 && (
            <div
              className={`accordion-item ${activeSection === 25 ? "active" : ""}`}
            >
              <button
                className="accordion-header"
                onClick={() => toggleSection(25)}
              >
                <span>Additional information</span>
                <span className="icon">{activeSection === 25 ? "−" : "+"}</span>
              </button>
              {activeSection === 25 && (
                <div className="accordion-content">
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <tbody>
                      {Object.entries(productData).map(([key, val]) => (
                        <tr key={key}>
                          <td
                            style={{
                              fontWeight: "bold",
                              padding: "10px",
                              borderBottom: "1px solid #eee",
                            }}
                          >
                            {key}
                          </td>
                          <td
                            style={{
                              padding: "10px",
                              borderBottom: "1px solid #eee",
                            }}
                          >
                            {val}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Sticky Cart ── */}
          <div className="sticky-bottom-cart">
            <div className="container-fluid-lg">
              <div className="row">
                <div className="col-12">
                  <div className="cart-content">
                    {/* Mobile Layout */}
                    <div className="mobile-cart-layout">
                      <div className="mobile-variant-name">
                        <h5
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            margin: 0,
                            lineHeight: "1.3",
                            wordBreak: "break-word",
                          }}
                        >
                          {selectedVariant
                            ? cleanVariantName(selectedVariant)
                            : productName}
                        </h5>
                      </div>
                      {selectedVariant?.price && (
                        <span
                          style={{
                            fontSize: "16px",
                            fontWeight: "700",
                            color: "#000",
                          }}
                        >
                          ₹{selectedVariant.price}
                        </span>
                      )}
                      <div className="mobile-cart-controls">
                        <div className="product-image-mobile">
                          {mainImage && (
                            <img
                              src={getImageUrl(mainImage)}
                              className="img-fluid"
                              alt={product.name}
                              style={{
                                width: "50px",
                                height: "50px",
                                objectFit: "contain",
                              }}
                            />
                          )}
                        </div>
                        <div className="cart-actions-mobile">
                          {!outOfStock && (
                            <div className="quantity-controls">
                              <button
                                className="qty-btn"
                                onClick={() => decreaseQuantity(product.id)}
                              >
                                -
                              </button>
                              <span className="qty-value">
                                {getQuantity(product.id)}
                              </span>
                              <button
                                className="qty-btn"
                                onClick={() => increaseQuantity(product.id)}
                              >
                                +
                              </button>
                            </div>
                          )}
                          <button
                            className="add-to-cart-mobile"
                            onClick={() => addToCart(product)}
                            disabled={outOfStock || addingToCart}
                          >
                            {outOfStock
                              ? "Out of Stock"
                              : addingToCart
                                ? "Adding..."
                                : "Add to Cart"}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Desktop Layout */}
                    <div className="desktop-cart-layout">
                      <div className="product-image">
                        {mainImage && (
                          <img
                            src={getImageUrl(mainImage)}
                            className="img-fluid blur-up lazyload"
                            alt={product.name}
                            style={{
                              width: "60px",
                              height: "60px",
                              objectFit: "contain",
                            }}
                          />
                        )}
                        <div className="content">
                          <h5
                            style={{
                              fontSize: "14px",
                              fontWeight: "600",
                              margin: "0 0 4px 0",
                              lineHeight: "1.3",
                            }}
                          >
                            {selectedVariant
                              ? cleanVariantName(selectedVariant)
                              : productName}
                          </h5>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginTop: "4px",
                              flexWrap: "wrap",
                              gap: "6px",
                            }}
                          >
                            {selectedVariant?.price && (
                              <span
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "700",
                                  color: "#000",
                                }}
                              >
                                ₹{selectedVariant.price}
                              </span>
                            )}
                            {selectedVariant?.mrp &&
                              selectedVariant.mrp > selectedVariant.price && (
                                <span
                                  style={{
                                    fontSize: "12px",
                                    color: "#999",
                                    textDecoration: "line-through",
                                  }}
                                >
                                  ₹{selectedVariant.mrp}
                                </span>
                              )}
                          </div>
                        </div>
                      </div>

                      {product.variants?.length > 0 && (
                        <div className="selection-section">
                          <select
                            className="form-control form-select"
                            value={selectedVariant?.id || ""}
                            onChange={(e) => {
                              const v = product.variants.find(
                                (v) => v.id === parseInt(e.target.value),
                              );
                              if (v) {
                                setSelectedVariant(v);
                                setSelectedFlavour(v.flavour);
                              }
                            }}
                            disabled={outOfStock}
                            style={{
                              padding: "10px 12px",
                              borderRadius: "8px",
                              border: "1px solid #ddd",
                              fontSize: "13px",
                              backgroundColor: "white",
                              cursor: "pointer",
                              width: "100%",
                            }}
                          >
                            <option value="" disabled>
                              Select Pack Size
                            </option>
                            {product.variants.map((variant) => {
                              const vOut = variantIsOut(variant);
                              const displayText = cleanVariantName(variant);
                              return (
                                <option
                                  key={variant.id}
                                  value={variant.id}
                                  disabled={vOut}
                                >
                                  {displayText} - ₹{variant.price}
                                  {vOut ? " (Out of Stock)" : ""}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      )}

                      <div className="shop-addtocart">
                        {outOfStock ? (
                          <button
                            className="add_btn"
                            disabled
                            style={{
                              background: "#ccc",
                              cursor: "not-allowed",
                              opacity: 0.6,
                              padding: "10px 20px",
                              borderRadius: "8px",
                              border: "none",
                              whiteSpace: "nowrap",
                              fontWeight: "600",
                            }}
                          >
                            Out of Stock
                          </button>
                        ) : (
                          <div className="desktop-cart-buttons">
                            <div className="shop-btn">
                              <button
                                onClick={() => decreaseQuantity(product.id)}
                                style={{
                                  width: "36px",
                                  height: "36px",
                                  borderRadius: "8px",
                                  border: "1px solid #ddd",
                                  background: "white",
                                  cursor: "pointer",
                                  fontSize: "18px",
                                  fontWeight: "600",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                -
                              </button>
                              <span
                                style={{
                                  minWidth: "30px",
                                  textAlign: "center",
                                  fontSize: "16px",
                                  fontWeight: "600",
                                }}
                              >
                                {getQuantity(product.id)}
                              </span>
                              <button
                                onClick={() => increaseQuantity(product.id)}
                                style={{
                                  width: "36px",
                                  height: "36px",
                                  borderRadius: "8px",
                                  border: "1px solid #ddd",
                                  background: "white",
                                  cursor: "pointer",
                                  fontSize: "18px",
                                  fontWeight: "600",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                +
                              </button>
                            </div>
                            <button
                              onClick={() => addToCart(product)}
                              disabled={addingToCart}
                              style={{
                                background: addingToCart ? "#666" : "#000",
                                cursor: addingToCart
                                  ? "not-allowed"
                                  : "pointer",
                                padding: "10px 24px",
                                borderRadius: "8px",
                                border: "none",
                                color: "white",
                                fontWeight: "600",
                                fontSize: "14px",
                                whiteSpace: "nowrap",
                                transition: "all 0.3s ease",
                              }}
                            >
                              {addingToCart ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  ></span>{" "}
                                  Adding...
                                </>
                              ) : (
                                "Add to Cart"
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <style>{`
                      @media (min-width: 769px) {
                        .desktop-cart-layout { display: flex; align-items: center; justify-content: space-between; gap: 20px; width: 100%; }
                        .desktop-cart-layout .product-image { display: flex; align-items: center; gap: 12px; flex: 2; min-width: 250px; }
                        .desktop-cart-layout .product-image .content { flex: 1; }
                        .desktop-cart-layout .selection-section { flex: 2; min-width: 220px; }
                        .desktop-cart-layout .shop-addtocart { flex: 1.5; min-width: 200px; }
                        .desktop-cart-buttons { display: flex; align-items: center; justify-content: flex-end; gap: 12px; width: 100%; }
                        .shop-btn { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
                        .shop-btn button:hover { background: #f5f5f5; border-color: #999; }
                        .add_btn:hover:not(:disabled) { background: #333 !important; }
                      }
                      @media (max-width: 768px) {
                        .desktop-cart-layout { display: none !important; }
                        .mobile-cart-layout { display: block !important; }
                      }
                      @media (min-width: 769px) and (max-width: 1024px) {
                        .desktop-cart-layout { gap: 12px; }
                        .desktop-cart-layout .product-image { min-width: 200px; flex: 1.5; }
                        .desktop-cart-layout .selection-section { min-width: 180px; flex: 1.5; }
                        .desktop-cart-layout .shop-addtocart { min-width: 180px; flex: 1; }
                        .desktop-cart-buttons { gap: 8px; }
                        .add_btn { padding: 8px 16px !important; font-size: 13px !important; }
                        .shop-btn button { width: 32px !important; height: 32px !important; }
                      }

                      
                    `}</style>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <style>{`

          @media (max-width: 768px) {
  .ingredient-grid {
    grid-template-columns: 1fr !important;
    justify-items: center !important;
    text-align: center;
  }
  .ingredient-card {
    width: 100%;
    max-width: 320px;
    margin: 0 auto;
  }
  .core-with .container {
    padding: 0 16px;
  }
  .core-with h2.title {
    font-size: 24px !important;
  }
}
            .mobile-cart-layout { display: none; }
            .desktop-cart-layout { display: flex; align-items: center; justify-content: space-between; gap: 20px; flex-wrap: wrap; }
            .desktop-cart-layout .product-image { display: flex; align-items: center; gap: 15px; flex: 2; min-width: 200px; }
            .desktop-cart-layout .selection-section { flex: 2; min-width: 200px; }
            .desktop-cart-layout .shop-addtocart { flex: 1; min-width: 150px; }
            .desktop-cart-layout .shop-btn { display: flex; align-items: center; justify-content: center; gap: 12px; margin-bottom: 10px; }
            @media (max-width: 768px) {
              .desktop-cart-layout { display: none !important; }
              .mobile-cart-layout { display: block !important; }
              .sticky-bottom-cart { position: fixed; bottom: 60px; left: 0; right: 0; background: white; box-shadow: 0 -2px 10px rgba(0,0,0,0.1); z-index: 998; padding: 12px 16px; border-top: 1px solid #eee; }
              .mobile-variant-name { margin-bottom: 10px; padding-bottom: 8px; border-bottom: 1px solid #f0f0f0; }
              .mobile-cart-controls { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 10px; }
              .product-image-mobile { display: flex; align-items: center; gap: 10px; flex: 1; }
              .price-mobile { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; }
              .cart-actions-mobile { display: flex; align-items: center; gap: 10px; }
              .quantity-controls { display: flex; align-items: center; gap: 8px; background: #f5f5f5; border-radius: 8px; padding: 4px; }
              .qty-btn { width: 32px; height: 32px; border: 1px solid #ddd; background: white; border-radius: 6px; cursor: pointer; font-size: 18px; font-weight: 600; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
              .qty-btn:hover { background: #f0f0f0; }
              .qty-value { min-width: 30px; text-align: center; font-size: 16px; font-weight: 600; }
              .add-to-cart-mobile { background: #000; color: white; border: none; padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; white-space: nowrap; transition: all 0.2s; }
              .add-to-cart-mobile:hover:not(:disabled) { background: #333; }
              .add-to-cart-mobile:disabled { background: #ccc; cursor: not-allowed; }
              .pack-selector-mobile { margin-top: 10px; width: 100%; }
              .pack-selector-mobile select { width: 100%; padding: 10px; border-radius: 8px; border: 1px solid #ddd; font-size: 13px; background: white; }
              body { padding-bottom: 120px !important; }
            }
            @media (max-width: 480px) {
              .sticky-bottom-cart { bottom: 55px; padding: 10px 12px; }
              .product-image-mobile img { width: 45px !important; height: 45px !important; }
              .qty-btn { width: 28px; height: 28px; font-size: 16px; }
              .qty-value { font-size: 14px; min-width: 25px; }
              .add-to-cart-mobile { padding: 6px 12px; font-size: 12px; }
              .price-mobile span { font-size: 14px !important; }
              body { padding-bottom: 110px !important; }
            }
            .mobile-bottom-nav { z-index: 1000 !important; position: fixed !important; bottom: 0 !important; }
          `}</style>
        </div>
      </section>

      <Footer />

      <style>{`
        .custom-toast{position:fixed;top:20px;right:20px;min-width:300px;max-width:400px;background:white;border-radius:8px;box-shadow:0 4px 12px rgba(0,0,0,.15);padding:16px 20px;z-index:9999;display:flex;align-items:center;justify-content:space-between;animation:slideIn .3s ease;border-left:4px solid}
        .custom-toast.success{border-left-color:#4caf50}.custom-toast.error{border-left-color:#f44336}.custom-toast.info{border-left-color:#2196f3}.custom-toast.warning{border-left-color:#ff9800}
        .toast-content{display:flex;align-items:center;gap:12px}.toast-content i{font-size:20px}
        .custom-toast.success .toast-content i{color:#4caf50}.custom-toast.error .toast-content i{color:#f44336}.custom-toast.info .toast-content i{color:#2196f3}.custom-toast.warning .toast-content i{color:#ff9800}
        .toast-content span{font-size:14px;color:#333}.toast-close{background:none;border:none;font-size:20px;cursor:pointer;color:#999;padding:0 5px}.toast-close:hover{color:#333}
        @keyframes slideIn{from{transform:translateX(100%);opacity:0}to{transform:translateX(0);opacity:1}}
        .shop-addtocart{margin-top:10px}.shop-btn{display:flex;align-items:center;justify-content:center;gap:5px}.shop-btn .btn{padding:5px 10px;font-size:14px}
        .add_btn{background:#000;color:#fff;border:none;padding:8px 15px;border-radius:5px;font-size:14px;cursor:pointer;transition:all .3s ease}.add_btn:hover{background:#333}
        .out-of-stock{opacity:.6;pointer-events:none}.disabled-btn{background:#ccc!important;cursor:not-allowed!important;opacity:.6}
        .product-thumbnails{margin-top:1rem;}
        .thumbnail-image{transition:all 0.3s ease;}
        .thumbnail-image:hover img{transform:scale(1.05);}
        .thumbnail-image.active img{border-color:#007bff !important;box-shadow:0 0 0 2px rgba(0,123,255,0.25);}
        @media(max-width:768px){.popup-modal{width:95%;padding:20px}}
        .form-select option:checked { background: #000000 !important; background-color: #000000 !important; color: #ffffff !important; }
        .login-popup-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(255, 255, 255, 0.7); backdrop-filter: blur(5px); display: flex; align-items: center; justify-content: center; z-index: 10000; animation: fadeIn 0.3s ease; }
        .login-popup-container { position: relative; max-width: 500px; width: 90%; max-height: 90vh; overflow-y: auto; background: transparent; border-radius: 20px; animation: slideUp 0.3s ease; }
        .login-popup-close { position: absolute; top: 15px; right: 15px; width: 36px; height: 36px; border-radius: 50%; background: white; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; z-index: 10; font-size: 16px; color: #333; transition: all 0.2s ease; box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
        .login-popup-close:hover { background: #f0f0f0; transform: scale(1.1); }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
    </>
  );
};

export default Productdetails;
